import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { FaSave, FaTimes, FaSpinner, FaTrash } from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";
import AsyncSelect from "react-select/async";

interface FormData {
  productId: string;
  customerId: string;
  amount: string;
  effectiveDate: string;
}

interface Product {
  id: string;
  name: string;
  product_code: string;
}

interface Customer {
  id: string;
  name: string;
}

const ProductsPriceForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL;
  const { logout } = useAuth();

  const [formData, setFormData] = useState<FormData>({
    productId: "",
    customerId: "",
    amount: "",
    effectiveDate: "",
  });

  const [products, setProducts] = useState<Product[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return {
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
      },
    };
  };

  const fetchData = async () => {
    try {
      const [productsRes, customersRes] = await Promise.all([
        axios.get(`${API}/products?limit=1000`, getAuthHeader()),
        axios.get(`${API}/customers`, getAuthHeader()),
      ]);
      setProducts(productsRes.data.data || []);
      setCustomers(customersRes.data.data || []);

      if (isEdit) {
        const res = await axios.get(`${API}/products-price/${id}`, getAuthHeader());
        const data = res.data;
        setFormData({
          productId: data.product_id || "",
          customerId: data.customer_id || "",
          amount: data.amount || "",
          effectiveDate: data.valid_from?.split("T")[0] || "",
        });
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        logout();
        navigate("/login");
      }
      alert("Error fetching data. Please try again.");
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (!user?.id || !user?.tenantId) {
      alert("User session expired. Please re-login.");
      return;
    }

    if (!formData.productId || !formData.amount || !formData.effectiveDate) {
      alert("Please fill all required fields.");
      return;
    }

    const confirmSave = window.confirm("Do you want to save this price?");
    if (!confirmSave) return;

    setLoading(true);

    try {
      const rawPayload = {
        product_id: formData.productId,
        customer_id: formData.customerId || null,
        valid_from: formData.effectiveDate,
        amount: parseInt(formData.amount.replace(/\./g, "")),
        tenant_id: user.tenantId,
        created_by_user: user.id,
      };

      const payload = Object.fromEntries(
        Object.entries(rawPayload).filter(([_, v]) => v !== "")
      );

      if (isEdit) {
        await axios.put(`${API}/products-price/${id}`, payload, getAuthHeader());
        alert("Price updated!");
      } else {
        await axios.post(`${API}/products-price`, payload, getAuthHeader());
        alert("Price created!");
      }

      navigate("/products-price");
    } catch (error: any) {
      const message = error?.response?.data?.message || "Failed to save price";
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmDelete = window.confirm("Are you sure you want to delete this price?");
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API}/products-price/${id}`, getAuthHeader());
      alert("Price deleted");
      navigate("/products-price");
    } catch (error) {
      console.error("Failed to delete price:", error);
      alert("Failed to delete price");
    }
  };

  const formatNumber = (value: string | number) => {
    const num = typeof value === "string" ? parseInt(value || "0") : value;
    return num.toLocaleString("id-ID");
  };

  const loadProductOptions = async (inputValue: string) => {
	  try {
		const res = await axios.get(`${API}/products/lookup`, {
		  ...getAuthHeader(),
		  params: {
			search: inputValue,
			limit: 1000, // agar semua hasil muncul
		  },
		});
		const items = res.data || [];
		return items.map((prod: Product) => ({
		  label: `${prod.product_code} - ${prod.name}`,
		  value: prod.id,
		}));
	  } catch (error) {
		return [];
	  }
  };
  
  const loadCustomerOptions = async (inputValue: string) => {
	  try {
		const user = JSON.parse(localStorage.getItem("user") || "{}");
		const res = await axios.get(`${API}/customers/lookup`, {
		  ...getAuthHeader(),
		  params: {
			search: inputValue,
			limit: 10000,
			tenantId: user?.tenantId || "", // ✅ tambahkan ini
		  },
		});
		const items = res.data || [];
		return items.map((cust: Customer) => ({
		  label: cust.name,
		  value: cust.id,
		}));
	  } catch (error) {
		console.error("Gagal load customer options", error);
		return [];
	  }
	};




  return (
    <div className="p-1 max-w-6xl mx-auto">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-gray-700">
            {isEdit ? "Edit Product Price" : "Add Product Price"}
          </h2>
          <div className="flex gap-2">
            <button
              type="submit"
              form="price-form"
              className="bg-blue-600 text-white px-2 py-1 rounded text-[13px] flex items-center gap-2 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? <FaSpinner className="animate-spin" /> : <FaSave />}
              Save
            </button>
            <button
              onClick={() => navigate("/products-price")}
              className="bg-gray-300 text-gray-700 px-2 py-1 rounded text-[13px] flex items-center gap-2"
            >
              <FaTimes />
              Cancel
            </button>
            {isEdit && (
              <button
                onClick={handleDelete}
                className="bg-red-600 text-white px-2 py-1 rounded text-[13px] flex items-center gap-2"
                disabled={loading}
              >
                <FaTrash />
                Delete
              </button>
            )}
          </div>
        </div>

        <form id="price-form" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <label className="text-[13px] font-medium mb-1">
                Product <span className="text-red-500">*</span>
              </label>
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={loadProductOptions}
                value={
                  formData.productId
                    ? {
                        value: formData.productId,
                        label:
                          products.find((p) => p.id === formData.productId)?.name ||
                          "Selected Product",
                      }
                    : null
                }
                onChange={(selected) =>
                  setFormData((prev) => ({
                    ...prev,
                    productId: selected?.value || "",
                  }))
                }
                placeholder="-- Search Product --"
                className="text-[13px]"
                styles={{
                  control: (base) => ({ ...base, minHeight: "36px" }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </div>

            <div className="flex flex-col">
              <label className="text-[13px] font-medium mb-1">Customer</label>
              <AsyncSelect
				  cacheOptions
				  defaultOptions
				  loadOptions={loadCustomerOptions}
				  value={
					formData.customerId
					  ? {
						  value: formData.customerId,
						  label:
							customers.find((c) => c.id === formData.customerId)?.name ||
							"Selected Customer",
						}
					  : null
				  }
				  onChange={(selected) =>
					setFormData((prev) => ({
					  ...prev,
					  customerId: selected?.value || "",
					}))
				  }
				  placeholder="-- Search Customer --"
				  isClearable
				  className="text-[13px]"
				  styles={{
					control: (base) => ({ ...base, minHeight: "36px" }),
					menu: (base) => ({ ...base, zIndex: 9999 }),
				  }}
			  />

            </div>

            <div className="flex flex-col">
              <label className="text-[13px] font-medium mb-1">
                Price (Rp) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="amount"
                value={formatNumber(formData.amount)}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/[^0-9]/g, "");
                  setFormData((prev) => ({ ...prev, amount: rawValue }));
                }}
                className="border rounded px-2 py-1 text-[13px] text-right"
                required
              />
            </div>

            <div className="flex flex-col">
              <label className="text-[13px] font-medium mb-1">
                Effective Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="effectiveDate"
                value={formData.effectiveDate}
                onChange={handleChange}
                className="border rounded px-2 py-1 text-[13px]"
                required
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductsPriceForm;
